import React from 'react';
import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSelector } from 'react-redux';

const ICON_BG_OPACITY = 0.88;
const OUTER_RING_OPACITY = 0.92;

export default function AccessDeniedCard({ title, variant = 'page' }) {
  const theme = useTheme();
  const rootUserEmail = useSelector((state) => state.org.org?.root_user_email ?? '');
  const email = rootUserEmail.trim();
  const isEmbedded = variant === 'embedded';

  const headline = ("You don't have access to {module}").replace(
    '{module}',
    title,
  );
  const description = 'Need access? Contact admin to update your permissions.';
  const contactAdminLabel = 'Contact admin';

  const iconOuterSize = isEmbedded ? 72 : 120;
  const iconInnerSize = isEmbedded ? 56 : 96;
  const iconFontSize = isEmbedded ? 28 : 44;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: isEmbedded ? 0 : '60vh',
        width: '100%',
        height: isEmbedded ? '100%' : undefined,
        px: isEmbedded ? 1 : 2,
        py: isEmbedded ? 1 : 4,
        background: isEmbedded
          ? 'transparent'
          : `linear-gradient(180deg, ${lighten(theme.palette.primary.main, 0.96)} 0%, ${theme.palette.background.default} 40%)`,
      }}
    >
      <Stack
        spacing={isEmbedded ? 1.5 : 3}
        alignItems="center"
        sx={{
          width: '100%',
          maxWidth: isEmbedded ? 360 : 560,
          textAlign: 'center',
        }}
      >
        <Fade in timeout={600}>
          <Box
            sx={{
              position: 'relative',
              width: iconOuterSize,
              height: iconOuterSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: iconOuterSize,
                height: iconOuterSize,
                borderRadius: '50%',
                bgcolor: lighten(theme.palette.primary.main, OUTER_RING_OPACITY),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  bgcolor: lighten(theme.palette.primary.main, OUTER_RING_OPACITY),
                  animation: 'accessDeniedPulse 3s ease-in-out infinite',
                },
                '@keyframes accessDeniedPulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 0.5,
                  },
                  '50%': {
                    transform: 'scale(1.08)',
                    opacity: 0.25,
                  },
                  '100%': {
                    transform: 'scale(1.15)',
                    opacity: 0,
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: iconInnerSize,
                height: iconInnerSize,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: lighten(theme.palette.primary.main, ICON_BG_OPACITY),
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: iconFontSize, color: 'primary.main' }} aria-hidden />
            </Box>
          </Box>
        </Fade>

        <Fade in timeout={800}>
          <Stack spacing={isEmbedded ? 1.5 : 3} alignItems="center" sx={{ width: '100%' }}>
            <Stack spacing={isEmbedded ? 0.75 : 1.5} sx={{ maxWidth: isEmbedded ? 320 : 440 }}>
              <Typography
                variant={isEmbedded ? 'subtitle1' : 'h5'}
                component={isEmbedded ? 'h2' : 'h1'}
                fontWeight={600}
                color="text.primary"
              >
                {headline}
              </Typography>
              <Typography variant={isEmbedded ? 'body2' : 'body1'} color="text.secondary">
                {description}
              </Typography>
            </Stack>

            {email ? (
              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  color="primary"
                  size={isEmbedded ? 'small' : 'medium'}
                  component="a"
                  href={`mailto:${email}`}
                >
                  {contactAdminLabel}
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Fade>
      </Stack>
    </Box>
  );
}
